import { useState } from 'react';
import { Search, ChevronDown, MoreVertical, Eye, FolderOpen, Download, Share2, Trash2, Heart } from 'lucide-react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { downloadImageWithWatermarks } from '../../lib/download-utils';
import { PageHeader } from './page-header';
import { PhotoBadge, PhotoCategory } from './photo-badge';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { DeleteFileDialog } from './delete-file-dialog';

interface Photo {
  id: string;
  url: string;
  category: PhotoCategory;
  propertyAddress: string;
  date: string;
  aspectRatio: 'landscape' | 'portrait' | 'square';
  roomName?: string;
  favorite?: boolean;
}

interface AllPhotosViewProps {
  allPhotos: Photo[];
  onPhotoClick?: (photo: Photo) => void;
  onGoToProperty?: (propertyAddress: string) => void;
  onDeletePhoto?: (photoUrl: string) => void;
  onToggleFavorite?: (photoUrl: string) => void;
  title?: string;
}

export function AllPhotosView({ allPhotos, onPhotoClick, onGoToProperty, onDeletePhoto, onToggleFavorite, title = "Wszystkie zdjęcia" }: AllPhotosViewProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'category'>('date');
  const [fileToDelete, setFileToDelete] = useState<{ url: string; name: string } | null>(null);

  // Filter photos based on search query
  const filteredPhotos = allPhotos.filter((photo) =>
    photo.propertyAddress.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort photos
  const sortedPhotos = [...filteredPhotos].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    } else {
      return a.category.localeCompare(b.category);
    }
  });

  return (
    <div className="min-h-full relative isolate">
      <PageHeader
        title={title}
        controls={
          <>
            {/* Search Bar */}
            <div className="relative w-full max-w-sm">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                strokeWidth={2}
              />
              <Input
                type="text"
                placeholder="Szukaj..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-8 text-[13px] bg-background border-border"
              />
            </div>

            {/* Sort Dropdown */}
            <Select value={sortBy} onValueChange={(value: 'date' | 'category') => setSortBy(value)}>
              <SelectTrigger className="w-[180px] h-9 text-[13px]">
                <SelectValue placeholder="Sortuj..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date" className="text-[13px]">
                  Sortuj: Najnowsze
                </SelectItem>
                <SelectItem value="category" className="text-[13px]">
                  Sortuj: Kategoria
                </SelectItem>
              </SelectContent>
            </Select>
          </>
        }
      />

      {/* Photo Grid */}
      <div className="px-4 lg:px-10 py-6 lg:py-10">
        {sortedPhotos.length > 0 ? (
          <ResponsiveMasonry
            columnsCountBreakPoints={{ 0: 1, 500: 2, 750: 3, 1000: 4, 1400: 5 }}
          >
            <Masonry gutter="12px">
              {sortedPhotos.map((photo) => (
                <div
                  key={photo.id}
                  className="relative group cursor-pointer overflow-hidden rounded-[6px] bg-white border border-border transition-all duration-200 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-0.5"
                  onClick={() => onPhotoClick?.(photo)}
                >
                  <img
                    src={photo.url}
                    alt={photo.propertyAddress}
                    className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105"
                    style={{
                      aspectRatio:
                        photo.aspectRatio === 'square' ? '1/1' : '16/10',
                    }}
                  />

                  {/* Category Badge Overlay */}
                  <div className="absolute top-3 left-3">
                    <PhotoBadge category={photo.category} />
                  </div>

                  {/* Context Menu - Top Right */}
                  <div className="absolute top-3 right-3 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity z-10 flex flex-col gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="flex items-center justify-center w-8 h-8 rounded-md bg-white/90 hover:bg-white backdrop-blur-sm shadow-sm transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                          className="gap-2 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPhotoClick?.(photo);
                          }}
                        >
                          <Eye className="w-4 h-4" strokeWidth={2} />
                          <span>Podgląd</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            onGoToProperty?.(photo.propertyAddress);
                          }}
                        >
                          <FolderOpen className="w-4 h-4" strokeWidth={2} />
                          <span>Przejdź do folderu</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadImageWithWatermarks(
                              photo.url,
                              `photo_${photo.id}.jpg`,
                            ).catch(err => console.error('Download error:', err));
                          }}
                        >
                          <Download className="w-4 h-4" strokeWidth={2} />
                          <span>Pobierz</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            alert('Funkcja udostępniania będzie dostępna wkrótce');
                          }}
                        >
                          <Share2 className="w-4 h-4" strokeWidth={2} />
                          <span>Udostępnij</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="gap-2 cursor-pointer text-red-600 dark:text-red-400 focus:text-red-700 dark:focus:text-red-300 focus:bg-red-50 dark:focus:bg-red-900/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFileToDelete({ url: photo.url, name: photo.roomName || 'Zdjęcie' });
                          }}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                          <span>Usuń</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Heart Button */}
                    <button
                      className={`flex items-center justify-center w-8 h-8 rounded-md bg-white/90 hover:bg-white backdrop-blur-sm shadow-sm transition-colors ${photo.favorite ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite?.(photo.url);
                      }}
                    >
                      <Heart className={`w-4 h-4 ${photo.favorite ? 'fill-current' : ''}`} strokeWidth={2} />
                    </button>
                  </div>

                  {/* Property Address Overlay - Shows on Hover */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-200">
                    <p className="text-white text-[11px] font-medium">
                      {photo.propertyAddress}
                    </p>
                    <p className="text-white/80 text-[10px] mt-0.5">
                      {photo.date}
                    </p>
                  </div>
                </div>
              ))}
            </Masonry>
          </ResponsiveMasonry>
        ) : (
          <div className="text-center py-12 text-muted-foreground text-[14px]">
            Nie znaleziono zdjęć pasujących do wyszukiwania.
          </div>
        )}
      </div>

      <DeleteFileDialog
        open={!!fileToDelete}
        onOpenChange={(open) => !open && setFileToDelete(null)}
        fileName={fileToDelete?.name || ''}
        onConfirm={() => {
          if (fileToDelete && onDeletePhoto) {
            onDeletePhoto(fileToDelete.url);
          }
          setFileToDelete(null);
        }}
      />
    </div>
  );
}